/**
 * CoSingery — **音樂風格目錄** —— ⭐ **唯一來源**（`decisions/034`；產品負責人 2026-09-24）
 *
 * 產品負責人原話：
 *   「when create a song, should also choose the type of music the user want, like K-POP, HK POP Music,
 *    Rap, Rock, Piano version, those main style in the market, but not all, and should able to add later,
 *    or initially can choose from a list, with full style, to put them in the list that to choose from quickly」
 *   ＋（裁決）「should be a quick mode that the AI auto select, user only choose if they click on self select」
 *   ＋「should be also give to the prompt **before writing the lyrics**, so that the lyrics will also have
 *      reference to follow」
 *
 * 設計：
 *   · **快速清單（12 個主流）** ＋ **全部清單（40 個）** → 快速清單由用戶自己加減（`localStorage`）
 *   · `prompt` ＝ **入 provider／歌詞 prompt 嘅英文**；`labelKey` ＝ 顯示（i18n，三語）
 *   · ⚠️ 本檔**只可以係純資料＋純函數**（唔准 import react／i18next）：Worker 直接讀呢個檔
 *     （先例：`functions/_lib/safety/crisis-mode.ts` 讀 `src/locales/*`）。
 *   · ⭐ **自動模式（預設）**：唔會多叫一次模型 —— 用**模型自己回嘅 `mood_tags`／歌詞**推導
 *     （`autoGenreFromTags()`，決定性、可單測）。要升級成「模型直接揀」＝ 另一條 Tier（見 `decisions/034`）。
 */

export interface GenreDef {
  /** 穩定 id（入 DB `songs.genre`；**唔准改**） */
  id: string;
  /** i18n key（三語字典要有） */
  labelKey: string;
  /** 入 provider／歌詞 prompt 嘅英文（顯示文字**唔准**當 prompt 用） */
  prompt: string;
  /** ⭐ 快速清單（預設顯示嗰 12 個） */
  quick?: boolean;
}

/** 風格目錄（**快速 12 個排最前**；其餘係「全部風格」） */
export const GENRES: GenreDef[] = [
  { id: 'kpop', labelKey: 'genre_kpop', prompt: 'K-pop, idol pop, catchy hook, punchy drums', quick: true },
  { id: 'cantopop', labelKey: 'genre_cantopop', prompt: 'Cantonese pop, HK pop, warm band arrangement', quick: true },
  { id: 'mandopop', labelKey: 'genre_mandopop', prompt: 'Mandopop, Mandarin pop ballad, strings', quick: true },
  { id: 'ballad', labelKey: 'genre_ballad', prompt: 'slow ballad, emotional, sparse piano', quick: true },
  { id: 'piano', labelKey: 'genre_piano', prompt: 'solo piano version, intimate, no drums', quick: true },
  { id: 'acoustic', labelKey: 'genre_acoustic', prompt: 'acoustic guitar, warm, unplugged feel', quick: true },
  { id: 'rock', labelKey: 'genre_rock', prompt: 'rock, driving electric guitars, live drums', quick: true },
  { id: 'rap', labelKey: 'genre_rap', prompt: 'rap, hip-hop beat, rhythmic flow', quick: true },
  { id: 'rnb', labelKey: 'genre_rnb', prompt: 'R&B, smooth groove, mellow chords', quick: true },
  { id: 'edm', labelKey: 'genre_edm', prompt: 'EDM, dance pop, big synth lead', quick: true },
  { id: 'lofi', labelKey: 'genre_lofi', prompt: 'lo-fi, soft beats, warm tape hiss', quick: true },
  { id: 'jazz', labelKey: 'genre_jazz', prompt: 'jazz, brushed drums, upright bass', quick: true },
  { id: 'citypop', labelKey: 'genre_citypop', prompt: 'city pop, 80s synth, groove bass' },
  { id: 'dreampop', labelKey: 'genre_dreampop', prompt: 'dream pop, reverb-drenched guitars' },
  { id: 'indie', labelKey: 'genre_indie', prompt: 'indie, raw band, intimate vocal' },
  { id: 'folk', labelKey: 'genre_folk', prompt: 'folk, fingerstyle guitar, storytelling' },
  { id: 'country', labelKey: 'genre_country', prompt: 'country, acoustic, slide guitar' },
  { id: 'blues', labelKey: 'genre_blues', prompt: 'blues, shuffle beat, soulful guitar' },
  { id: 'soul', labelKey: 'genre_soul', prompt: 'soul, warm organ, expressive vocal' },
  { id: 'funk', labelKey: 'genre_funk', prompt: 'funk, slapped bass, tight horns' },
  { id: 'disco', labelKey: 'genre_disco', prompt: 'disco, four-on-the-floor, string stabs' },
  { id: 'house', labelKey: 'genre_house', prompt: 'house, four-on-the-floor, piano stabs' },
  { id: 'techno', labelKey: 'genre_techno', prompt: 'techno, hypnotic loop, dark synth' },
  { id: 'trap', labelKey: 'genre_trap', prompt: 'trap, 808 bass, hi-hat rolls' },
  { id: 'dnb', labelKey: 'genre_dnb', prompt: 'drum and bass, fast breakbeat, deep bass' },
  { id: 'reggae', labelKey: 'genre_reggae', prompt: 'reggae, off-beat skank, laid-back' },
  { id: 'latin', labelKey: 'genre_latin', prompt: 'latin, percussive, nylon guitar' },
  { id: 'bossanova', labelKey: 'genre_bossanova', prompt: 'bossa nova, soft nylon guitar, brushed kit' },
  { id: 'ambient', labelKey: 'genre_ambient', prompt: 'ambient, pads, no percussion' },
  { id: 'chill', labelKey: 'genre_chill', prompt: 'chill, slow groove, soft keys' },
  { id: 'unplugged', labelKey: 'genre_unplugged', prompt: 'unplugged, live acoustic band' },
  { id: 'metal', labelKey: 'genre_metal', prompt: 'metal, distorted riff, heavy drums' },
  { id: 'punk', labelKey: 'genre_punk', prompt: 'punk, fast power chords, raw energy' },
  { id: 'synthwave', labelKey: 'genre_synthwave', prompt: 'synthwave, retro arpeggio, gated drums' },
  { id: 'orchestral', labelKey: 'genre_orchestral', prompt: 'orchestral, cinematic strings, timpani' },
  { id: 'classical', labelKey: 'genre_classical', prompt: 'classical, chamber strings, piano' },
  { id: 'musical', labelKey: 'genre_musical', prompt: 'musical theatre, big vocal, show arrangement' },
  { id: 'anime', labelKey: 'genre_anime', prompt: 'anime opening, energetic band, bright lead' },
  { id: 'chinesestyle', labelKey: 'genre_chinesestyle', prompt: 'Chinese style, guzheng, pentatonic melody' },
  { id: 'lullaby', labelKey: 'genre_lullaby', prompt: 'lullaby, music box, soft humming' },
];

/** 快速清單（預設）＝ 目錄入面 `quick: true` 嗰批 */
export const DEFAULT_QUICK_GENRE_IDS: readonly string[] = GENRES.filter((g) => g.quick).map((g) => g.id);

/** ⭐ 自動模式（`songs.genre_source='auto'`；`genre` 仍然係一個真 slug） */
export const GENRE_AUTO = 'auto';

export function genreDef(id: string | null | undefined): GenreDef | null {
  if (!id) return null;
  return GENRES.find((g) => g.id === id) ?? null;
}

/** 白名單（唔認識嘅值 → `null`；**唔准**亂傳入 prompt） */
export function parseGenre(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const id = value.trim();
  if (!id || id === GENRE_AUTO) return null;
  return genreDef(id) ? id : null;
}


/** 入 prompt 嘅英文（唔認識 → `null`） */
export function genrePromptFor(id: string | null | undefined): string | null {
  return genreDef(id)?.prompt ?? null;
}

/** 顯示用 i18n key（唔認識 → `null`） */
export function genreLabelKey(id: string | null | undefined): string | null {
  return genreDef(id)?.labelKey ?? null;
}

/**
 * ⭐ **自動揀風格**（產品負責人：「a quick mode that the AI auto select」）——
 *   用**模型自己回嘅 `mood_tags`／歌詞關鍵字**推導（**決定性**；唔會多叫模型、唔多花錢）。
 *   ⚠️ 認唔到 → 跟語言：粵語 → `cantopop`；其餘 → `mandopop`（貼近市場主流）。
 */
export function autoGenreFromTags(tags: string[], opts: { language?: string | null } = {}): string {
  const text = (tags ?? []).join(',');
  const has = (...words: string[]) => words.some((w) => text.includes(w));
  if (has('嬲', '怒', '不甘', '火滾', '唔公平')) return 'rock';
  if (has('緊張', '焦慮', '心急', '壓力', '擔心')) return 'lofi';
  if (has('興奮', '熱血', '衝', '幹勁', '澎湃')) return 'edm';
  if (has('慶祝', '成功', '驕傲', '達標', '贏')) return 'kpop';
  if (has('思念', '懷念', '掛住', '想念', '回憶')) return 'ballad';
  if (has('感恩', '溫暖', '窩心', '甜')) return 'acoustic';
  if (has('攰', '累', '疲', '失眠', '想休息')) return 'piano';
  if (has('平靜', '靜', '安心')) return 'ambient';
  const lang = (opts.language ?? '').toLowerCase();
  return lang === 'zh-hk' || lang === 'yue' ? 'cantopop' : 'mandopop';
}

/**
 * 把風格 prompt **併入** provider 曲風字串（**唯一合併點**；`flow.ts` 用）
 *   · 已經有同一款字眼 → **唔重複加**（唔准出 `K-pop, K-pop, …`）
 *   · 冇風格 → 原樣返回（**唔准**亂塞預設）
 */
export function mergeGenrePrompt(genrePrompt: string | null, providerPrompt: string): string {
  const g = (genrePrompt ?? '').trim();
  const p = (providerPrompt ?? '').trim();
  if (!g) return p;
  if (!p) return g;
  const head = g.split(',')[0].trim().toLowerCase();
  if (head && p.toLowerCase().includes(head)) return p;
  return `${g}, ${p}`;
}

/**
 * 歌詞 prompt 用嘅**曲風指示**（產品負責人：「should be also give to the prompt **before writing the
 *   lyrics**, so that the lyrics will also have reference to follow」）
 *   · 用戶揀咗 → 明文寫出風格＋要求歌詞配合（例：rap 句短促、K-pop 副歌要夠 hook）
 *   · 自動（`null`）→ **唔指定**（由 `autoGenreFromTags()` 推導；歌詞唔受阻）
 */
export function genreLyricHint(id: string | null | undefined): string {
  const prompt = genrePromptFor(id);
  if (!prompt) return '';
  return `曲風（用戶揀咗）：${prompt}。歌詞要配合呢個曲風嘅節奏同語氣（例：rap 句短促、hook 要夠搶；ballad 可以慢同鬆）。`;
}
